import { Button, DrawerFooter, Text, useToast, VStack } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import {
  isValidEmailFunc,
  isValidName,
  isValidPhoneFunc,
} from "../../../utils";
import {
  createUser,
  fetchRoles,
  loadDealershipsApi,
} from "../../../utils/api/users.api";
import { CustomRadio, Drawer, Input, PhoneInput } from "../../common";
import SwitchToggle from "../../common/Switch";
import DebouncedAsyncSelect from "../../common/DebouncedAsyncSelect";

type data = {
  isSimpSocial?: boolean;
  isOpen: boolean;
  onClose: any;
  refetchParent: any;
  isFromDealershipMenu?: boolean;
};
type permission = {
  allowed: boolean;
  display_name: string;
  module_key: string;
};

interface ApiResult {
  data: any;
}
interface ApiRequest {
  user: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    dealership_ids: number[];
    user_role_attributes: {
      role_id: number;
      permissions: permission[];
    };
  };
}
const CreateUsers = (props: data) => {
  const { isSimpSocial, isOpen, onClose, refetchParent, isFromDealershipMenu } =
    props;
  const toast = useToast();

  //..........api calling..............
  const { data, isLoading } = useQuery({
    queryKey: ["rolesforCreateUser"],
    queryFn: () =>
      isSimpSocial
        ? fetchRoles({
            simp_social_user: isSimpSocial,
          })
        : fetchRoles(),
  });

  const [name, setName] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dealerships, setDealerships] = useState<SelectOptionProps[]>([]);
  const [role, setRole] = useState("");
  const [roleOptions, setRoleOptions] = useState<SelectOptionProps[]>([]);
  const [permissionOptions, setPermissionOptions] = useState<permission[]>([]);

  //.......error states......
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [lastnameError, setLastnameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [dealershipError, setDealershipError] = useState("");

  useEffect(() => {
    setRole(data?.roles?.[0]?.id.toString());

    setRoleOptions(
      data?.roles?.map((role: any) => ({
        value: role.id.toString(),
        label: role.title,
      }))
    );

    if (Array.isArray(data?.roles[0]?.permissions)) {
      setPermissionOptions(
        data?.roles[0]?.permissions?.map((permission: any) => ({
          allowed: permission.allowed,
          display_name: permission.display_name,
          module_key: permission.module_key,
        }))
      );
    }
  }, [data]);

  const mutation = useMutation<ApiResult, Error, ApiRequest, unknown>({
    mutationFn: async (payload) => {
      try {
        const response = await createUser(payload);
        return { data: response.data };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      refetchParent();
      onClose();
      toast({
        description: "User created successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
    onError: (error: any) => {
      toast({
        description: `Error creating user: ${
          error?.response?.data?.errors?.toString() ?? "Something went wrong"
        }`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const isValidDealership = () => {
    if (isSimpSocial || isFromDealershipMenu) {
      setDealershipError("");
      return true;
    }

    if (!dealerships.some((dealership) => dealership.value)) {
      setDealershipError("Please select at least one dealership.");
      return false;
    }

    setDealershipError("");
    return true;
  };

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    let fnameError = isValidName(name, setNameError, "First Name");
    let lnameError = isValidName(lastname, setLastnameError, "Last Name");
    let emailError = isValidEmailFunc(email, setEmailError);
    let phoneError = isValidPhoneFunc(phone, setPhoneError);
    let dealershipError = isValidDealership();

    console.log("dealershipError", dealershipError);

    if (
      !fnameError ||
      !lnameError ||
      !emailError ||
      !phoneError ||
      !dealershipError
    ) {
      return;
    }

    const requestData: ApiRequest = {
      user: {
        email: email,
        first_name: name,
        last_name: lastname,
        phone: phone,
        dealership_ids: dealerships.map((d) => Number(d.value)),
        user_role_attributes: {
          role_id: Number(role),
          permissions: permissionOptions,
        },
      },
    };

    mutation.mutate(requestData);
  };

  const handlePermissionToggle = (moduleKey: string, isChecked: boolean) => {
    setPermissionOptions((prevPermissions: permission[]) => {
      return prevPermissions?.map((permission: permission) =>
        permission.module_key === moduleKey
          ? { ...permission, allowed: isChecked }
          : permission
      );
    });
  };

  const loadDealerships = async (inputValue: string) => {
    try {
      const response = await loadDealershipsApi({
        status: "active",
        searchValue: inputValue,
      });

      const options = response?.dealerships?.map((dealership: any) => ({
        value: dealership.id.toString(),
        label: dealership.name,
      }));
      return options;
    } catch (error) {
      console.error("Error fetching dealership data:", error);
      return [];
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Create User"
      isLoading={isLoading || mutation?.isLoading}
    >
      <VStack alignItems="flex-start" padding="1rem 1.5rem" w="100%" gap="1rem">
        <Input
          placeholder="Enter First Name"
          label="First Name"
          onChange={(e: any) => {
            setNameError("");
            setName(e.target.value);
          }}
          value={name}
          isRequired={true}
          error={nameError}
        />
        <Input
          placeholder="Enter Last Name"
          label="Last Name"
          onChange={(e: any) => {
            setLastnameError("");
            setLastname(e.target.value);
          }}
          value={lastname}
          isRequired={true}
          error={lastnameError}
        />

        <Input
          placeholder="Enter email address"
          label="Email"
          onChange={(e: any) => {
            setEmailError("");
            setEmail(e.target.value);
          }}
          value={email}
          isRequired={true}
          error={emailError}
        />

        <PhoneInput
          label="Phone number"
          placeholder="Enter phone number"
          onChange={(value) => {
            setPhoneError("");
            setPhone(value);
          }}
          isRequired={false}
          value={phone}
          error={phoneError}
        />

        {!isSimpSocial && !isFromDealershipMenu && (
          <>
            <Text
              textStyle="h4"
              fontWeight="600"
              fontSize="0.875rem"
              color="var(--grey-900)"
            >
              Assign Dealerships
            </Text>
            <DebouncedAsyncSelect
              loadOptions={(inputValue: string) => loadDealerships(inputValue)}
              onChange={(value: any) => setDealerships(value)}
              placeholder="Enter the dealerships"
              isMulti={true}
              defaultOptions
              styles={{
                control: (provided: any, state: any) => ({
                  ...provided,
                  minWidth: "442px",
                  padding: "6px 7px",
                  borderRadius: "0.5rem",
                  border: `1px solid var(--grey-300)`,
                  marginTop: "-10px",
                  borderColor: state.isFocused
                    ? "#ddd"
                    : !dealershipError
                    ? "var(--grey-300)"
                    : "var(--red-600)",
                  // overwrittes hover style
                  "&:hover": {
                    borderColor: state.isFocused
                      ? "#ddd"
                      : !dealershipError
                      ? "var(--grey-300)"
                      : "var(--red-600)",
                  },
                }),
              }}
            />

            <Text textStyle="captionSmall" color="var(--red-600)">
              {dealershipError}
            </Text>
          </>
        )}

        <CustomRadio
          label="Place choose an access level for the user"
          direction="column"
          options={roleOptions}
          value={role}
          setValue={(value: string) => {
            setRole(value);
            const selectedRole = data?.roles.find(
              (r: any) => r.id.toString() === value
            );
            setPermissionOptions(
              selectedRole?.permissions?.map((permission: permission) => ({
                allowed: permission.allowed,
                display_name: permission.display_name,
                module_key: permission.module_key,
              }))
            );
          }}
        />

        {permissionOptions?.length && (
          <VStack
            padding="1rem"
            background="var(--grey-100)"
            border="1px solid var(--grey-300)"
            borderRadius="0.5rem"
            gap="1rem"
            w="100%"
            alignItems="flex-start"
          >
            <Text textStyle="h6" color="var(--grey-800)">
              Set custom permissions below
            </Text>

            {permissionOptions?.map((permission: any) => (
              <SwitchToggle
                key={permission.module_key}
                defaultChecked={permission.allowed}
                label={permission.display_name}
                onChange={(isChecked) =>
                  handlePermissionToggle(permission.module_key, isChecked)
                }
              />
            ))}
          </VStack>
        )}
      </VStack>
      <DrawerFooter
        position="absolute"
        bottom="0"
        left="0"
        width="100%"
        borderTop="1px solid var(--grey-300)"
        display="flex"
        alignItems="center"
        gap="0.81rem"
      >
        <Button variant="outline" w="100%" size="xl" onClick={onClose}>
          Cancel
        </Button>
        <Button w="100%" size="xl" onClick={handleSubmit}>
          Create User
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

export default CreateUsers;
